// subscribe.component.ts
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  effect
} from '@angular/core';
import { Router } from '@angular/router';

import {
  Stripe,
  StripeElements,
  StripePaymentElement,
  Appearance
} from '@stripe/stripe-js';
import { firstValueFrom } from 'rxjs';

import { StripeService } from 'app/services/stripe.service';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { environment } from 'env/environment';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { holdPls } from 'app/utils/time/timing-utils';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { AppStateService } from 'app/services/app-state.service';

export const SUBSCRIPTION_CONFIG = {
  priceId: environment.subscriptionPriceId,
  name: 'TailOne Annual Membership',
  description: 'Unlimited Access for 365 Days'
};

@Component({
  selector: 'app-subscribe',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PrimaryBtnComponent,
    PageWrapperComponent,
    SpinnerComponent,
    SvgLoaderComponent
  ],
  templateUrl: './subscribe.component.html',
  styleUrls: ['./subscribe.component.scss']
})
export class SubscribeComponent implements AfterViewInit {
  @ViewChild('paymentInfo', { static: true })
  paymentInfo!: ElementRef<HTMLDivElement>;

  subscription = SUBSCRIPTION_CONFIG;
  isLoading: boolean = true;
  isProcessingPayment: boolean;
  hasComplimentaryAccess = false;

  private stripe: Stripe | null = null;
  private elements?: StripeElements;
  private paymentElement?: StripePaymentElement;
  private paymentInitPromise: Promise<void> | null = null;

  constructor(
    private stripeService: StripeService,
    private router: Router,
    private appState: AppStateService
  ) {
    effect(() => {
      const entitlement = this.appState.user.entitlement();
      console.log('entitlement on sub', entitlement)
      if (!entitlement) {
        return;
      }
      if (entitlement.reason === 'free-access-granted' || entitlement.reason === 'subscription-active') {
        this.router.navigate(['/account']);
      }
    });
  }

  async ngAfterViewInit() {
    this.initializePaymentElement();
  }

  async onSubmit() {
    if (!this.stripe || !this.elements) {
      return;
    }

    this.isProcessingPayment = true;

    setTimeout(() => {
      this.isLoading = true;
    }, 2000);

    const { error, paymentIntent } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: `${window.location.origin}/account`
      },
      redirect: 'if_required'
    });

    if (error) {
      // If the PI already succeeded, treat as success
      if (
        error.code === 'payment_intent_unexpected_state' &&
        paymentIntent?.status === 'succeeded'
      ) {
        await holdPls(2500);
        this.router.navigate(['/account']);
      } else {
        console.error('Payment confirmation failed', error);
        this.isProcessingPayment = false;
        this.isLoading = false;
      }
    } else if (paymentIntent?.status === 'succeeded') {
      await holdPls(2500);
      // Normal success path
      this.router.navigate(['/account']);
      this.isProcessingPayment = false;
      this.isLoading = false;
    }
  }

  private async initializePaymentElement(): Promise<void> {
    try {
      if (this.hasComplimentaryAccess) {
        this.isLoading = false;
        return;
      }

      if (!this.stripe) {
        this.stripe = await this.stripeService.getStripe();
      }

      if (!this.stripe) {
        console.error('Stripe.js failed to load');
        alert('Payment system is not available at this time.');
        this.isLoading = false;
        return;
      }

      const { clientSecret } = await firstValueFrom(
        this.stripeService.createSubscription(this.subscription.priceId)
      );

      if (this.hasComplimentaryAccess) {
        this.isLoading = false;
        this.router.navigate(['/account']);
        return;
      }

      const appearance: Appearance = {
        theme: 'night',
        variables: {
          colorPrimary: '#1f2630',
          colorTextPlaceholder: '#6D7485',
        },
        rules: {
          '.Input': {
            backgroundColor: '#21282f',
            border: '1px solid transparent'
          },
          '.Tab': {
            border: '1px solid transparent',
            backgroundColor: '#1b1f28',
          },
        }
      };

      this.elements = this.stripe.elements({ clientSecret, appearance });
      this.paymentElement = this.elements.create('payment');
      this.paymentElement.mount(this.paymentInfo.nativeElement);
      await holdPls(875);
      this.isLoading = false;
    } catch (err) {
      console.error('Error setting up Payment Element', err);
      alert('Unable to connect payments.');
      this.isLoading = false;
      this.paymentElement = undefined;
      this.elements = undefined;
    }
  }
}
